import { useMemo } from 'react';
import moment from 'moment';
import { NeedsList } from '../../../types/needs';
import { TableColumn, StatusTag } from '../../shared/table';
import { InitialsAvatar } from '../../shared';

interface UseNeedsTableConfigOptions {
  isGroupPage?: boolean;
}

export const useNeedsTableConfig = (options: UseNeedsTableConfigOptions = {}): TableColumn<NeedsList>[] => {
  const { isGroupPage = false } = options;
  
  return useMemo<TableColumn<NeedsList>[]>(() => [
    {
      header: isGroupPage ? 'Need List Name' : 'Need List Name / Group',
      width: '220px',
      flexGrow: true,
      renderCell: (needsList) => (
        <div className="flex flex-col">
          <span className="text-sm font-semibold">
            {needsList.name}
          </span>
          {!isGroupPage && needsList.group && (
            <span className="text-xs text-gray-500">{needsList.group}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Coordinator',
      width: '160px',
      renderCell: (needsList) => (
        <div className="flex items-center gap-2">
          <InitialsAvatar 
            personId={needsList.coordinatorId} 
            name={needsList.coordinator}
            size={24} 
          />
          <span className="text-sm truncate">{needsList.coordinator}</span>
        </div>
      ),
    },
    {
      header: 'Next Item Needed',
      width: '200px',
      flexGrow: true,
      renderCell: (needsList) => (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold">
            {needsList.nextItemNeeded}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">
              {moment(needsList.nextItemNeededDate).format('MMM D, YYYY')}
            </span>
            <span className="text-xs text-gray-400">•</span>
            <span 
              className={`text-xs font-medium tabular-nums ${
                needsList.itemsOutstandingQty > 0 
                  ? 'text-red-600 font-semibold' 
                  : 'text-green-600'
              }`}
            >
              {needsList.itemsOutstandingQty} outstanding
            </span>
          </div>
        </div>
      ),
    },
    {
      header: 'Status',
      width: '160px',
      renderCell: (needsList) => (
        <StatusTag 
          variant={needsList.status === 'Outstanding Needs' ? 'warning' : 'success'} 
          label={needsList.status} 
          dataTarget={`needs-status-${needsList.id}`}
        />
      ),
    },
  ], [isGroupPage]);
};
